import { observable, makeObservable, action } from 'mobx';
import { SEARCH_PARAMS } from '../utils/constants';
import { RootStore } from './root';
import { ROUTES } from '../routes';
import { FLOW_EMAIL_STORAGE_KEY, SIGNUP_EMAIL_STORAGE_KEY } from './constants';
import { normalizeEmail } from '../utils/general';
import { organizationIdOf } from '../utils/requestedOrganization';

export const SSO_PROVIDERS = {
  YELL: 'YELL',
};

/** The flow's email, and the account picked (or IdP-named) for it. */
type SavedFlow = { email: string; organizationId?: string };

const readSavedFlow = (): SavedFlow | undefined => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(FLOW_EMAIL_STORAGE_KEY)!);
    return typeof saved?.email === 'string' && saved.email ? saved : undefined;
  } catch (_) {
    return undefined;
  }
};

type ResolvedAccount = {
  email: string;
  organizationId: string;
  pickedInSelector: boolean;
  hasPassword?: boolean;
  namedByIdp: boolean;
};

export class UserDataStore {
  private readonly rootStore: RootStore;
  public email: string;
  public currentLoggedInEmail: string;
  /**
   * Organization of the account an email resolved to, with that email: the
   * login form lets the email be edited without a new lookup.
   */
  private resolvedAccount?: ResolvedAccount;
  /**
   * The flow as this tab's previous page left it, kept in sessionStorage: a
   * reload of a later screen loses the store, and the email step goes on
   * from it.
   */
  readonly savedFlowBeforeReload = readSavedFlow();

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.currentLoggedInEmail = window.__CURRENT_LOGGED_IN_USER_EMAIL__;
    this.email = this.getInitialEmailValue();
    makeObservable<UserDataStore, 'resolvedAccount'>(this, {
      email: observable,
      resolvedAccount: observable.ref,
      setEmail: action.bound,
      setResolvedAccount: action.bound,
      clearResolvedAccount: action.bound,
      dropPickedAccount: action.bound,
    });
  }

  setEmail(email: string) {
    this.email = email;
    this.saveFlow();
  }

  setResolvedAccount(
    email: string,
    account?: {
      parentAccountId?: string;
      userAuthenticatorsData?: { hasPassword?: boolean };
    },
    { pickedInSelector = false, namedByIdp = false } = {},
  ) {
    this.resolvedAccount = account
      ? {
          email: normalizeEmail(email),
          organizationId: organizationIdOf(account),
          pickedInSelector,
          // A present authenticators object with the field dropped means
          // false, as `!hasPassword` reads it everywhere else. No object at
          // all means the lookup told us nothing, which must stay unknown.
          hasPassword: account.userAuthenticatorsData
            ? Boolean(account.userAuthenticatorsData.hasPassword)
            : undefined,
          namedByIdp,
        }
      : undefined;
    this.saveFlow();
  }

  clearResolvedAccount() {
    this.resolvedAccount = undefined;
    this.saveFlow();
  }

  /**
   * Forgets the account picked in the account selector, or named by an IdP
   * login. They live only here, not in the URL, so a page load forgets them
   * too.
   */
  dropPickedAccount() {
    if (
      this.resolvedAccount?.pickedInSelector ||
      this.resolvedAccount?.namedByIdp
    ) {
      this.resolvedAccount = undefined;
      this.saveFlow();
    }
  }

  /** The resolved account's organization, only while logging in with the email it was resolved for. */
  organizationIdFor(email?: string) {
    return email && this.resolvedAccount?.email === normalizeEmail(email)
      ? this.resolvedAccount.organizationId
      : undefined;
  }

  /**
   * Whether the resolved account has a password, only while logging in with
   * the email it was resolved for. undefined when no lookup has answered, or
   * when the email on the form has moved on from the one it answered for.
   */
  hasPasswordFor(email?: string) {
    return email && this.resolvedAccount?.email === normalizeEmail(email)
      ? this.resolvedAccount.hasPassword
      : undefined;
  }

  /** As organizationIdFor, only for an account picked in the account selector. */
  pickedOrganizationIdFor(email?: string) {
    return this.resolvedAccount?.pickedInSelector
      ? this.organizationIdFor(email)
      : undefined;
  }

  /**
   * As organizationIdFor, for an account picked in the account selector or
   * named by an IdP login, whose merge continues in it.
   */
  namedOrganizationIdFor(email?: string) {
    return this.resolvedAccount?.pickedInSelector ||
      this.resolvedAccount?.namedByIdp
      ? this.organizationIdFor(email)
      : undefined;
  }

  private saveFlow() {
    if (!this.email) {
      return;
    }
    const flow: SavedFlow = {
      email: this.email,
      organizationId: this.namedOrganizationIdFor(this.email),
    };
    try {
      sessionStorage.setItem(FLOW_EMAIL_STORAGE_KEY, JSON.stringify(flow));
    } catch (_) {}
  }

  private getInitialEmailValue() {
    if (
      this.rootStore.navigationStore.currentRoute === ROUTES.SIGNUP_EMAIL_CODE
    ) {
      try {
        const email = sessionStorage.getItem(SIGNUP_EMAIL_STORAGE_KEY);
        if (email) {
          return email;
        }
      } catch (_) {}
    }
    const emailFromUrl =
      this.rootStore.navigationStore.getQueryParam(SEARCH_PARAMS.EMAIL) ||
      this.rootStore.navigationStore.getQueryParam(
        SEARCH_PARAMS.DEFAULT_EMAIL,
      ) ||
      '';
    return emailFromUrl.replace(/ /g, '+');
  }
}
